/**
 * Forward Challenge Strategy Implementation
 * Handles the traditional "X of Y major" challenge format
 */

import { Challenge, ChallengeType, ChallengeStrategy } from './challenge-types.js';
import { MusicTheory } from './music-theory.js';

export class ForwardChallengeStrategy implements ChallengeStrategy {
  /**
   * Generate a new forward challenge
   * @param key - Optional specific key to use, otherwise random
   * @returns Generated forward challenge
   */
  generateChallenge(key?: string): Challenge {
    const challengeKey = key || MusicTheory.getRandomKey();
    const degree = MusicTheory.getRandomDegree();
    const note = MusicTheory.getScaleDegree(challengeKey, degree);

    return {
      type: ChallengeType.FORWARD,
      key: challengeKey,
      degree,
      note,
      correctAnswer: note,
      timestamp: Date.now()
    };
  }

  /**
   * Format the challenge text for display in the UI
   * @param challenge - The challenge to format
   * @returns Display text string in "X of Y major" format
   */
  formatDisplayText(challenge: Challenge): string {
    return `${challenge.degree} of ${challenge.key} major`;
  }

  /**
   * Format the challenge text for speech synthesis (TTS)
   * @param challenge - The challenge to format
   * @returns Speech text string with proper pronunciation
   */
  formatSpeechText(challenge: Challenge): string {
    return `${challenge.degree} of ${MusicTheory.getSpeakableKey(challenge.key)}`;
  }

  /**
   * Validate a user's answer against the challenge
   * @param challenge - The challenge being answered
   * @param answer - User's answer (note name)
   * @returns True if the answer is correct
   */
  validateAnswer(challenge: Challenge, answer: string | null): boolean {
    return MusicTheory.isCorrectAnswer(challenge.key, challenge.degree, answer);
  }

  /**
   * Get the correct answer in speech format for TTS feedback
   * @param challenge - The challenge that was answered incorrectly
   * @returns Speech text for the correct answer
   */
  getCorrectAnswerSpeech(challenge: Challenge): string {
    return `Correct answer: ${MusicTheory.getSpeakableNote(challenge.correctAnswer)}`;
  }
}